import './members.less'

import {Child, Children} from 'mithril'
import {Country, Member, MemberType} from 'types'
import {Filters, Toggle} from 'views/filter'
import {Maps, Marker} from 'views/maps'
import {Picture, View, m} from '@codeurs/front'

import {Aside} from 'views/layout/aside'
import {Filter} from 'util/filter'
import Layout from 'views/layout'
import {MAPS_API_KEY} from 'config'
import {PaginationThemed} from 'views/pagination'
import Title from 'views/layout/title'
import jump from 'jump.js'
import {pagedFilters} from 'views/loader/pagedloader'
import scrollIntoView from 'scroll-into-view-if-needed'

type CountryWithMembers = Country & {
	members: Array<Member>
}

const toPosition = ({latitude, longitude}) => ({
	lat: parseFloat(latitude),
	lng: parseFloat(longitude)
})

class MembersMap extends View<{
	countries: Array<CountryWithMembers>
	selected: Filter<number>
}> {
	render = this.render

	view() {
		const {countries, selected} = this.attrs
		return m('.membersmap',
			m(Maps,
				{
					apiKey: MAPS_API_KEY,
					center: {lat: 50.3115184, lng: 6.3301855},
					zoom: 4.5,
					disableDefaultUI: true
				},
				countries
					.filter(country => country.members.length > 0)
					.map(country =>
						m(Marker, {
							position: toPosition(country),
							on: {
								click: () => {
									selected.add(country.id)
									jump('.members-overview')
								}
							}
						})
					)
			)
		)
	}
}

class MembersMember extends View<Member & {countryRef: Country}> {
	render = this.render

	view() {
		const {
			title,
			contact_name,
			email,
			website,
			address,
			postal,
			city,
			phone,
			countryRef,
			image
		} = this.attrs
		return (
			countryRef &&
			m('.membersmember', [
				image &&
					!image.empty &&
					m('.membersmember-image',
						m(Picture, {style: {display: 'inline-block'}, ...image})
					),
				m('.membersmember-desc', [
					m('.membersmember-desc-title', [
						title,
						m('span.membersmember-desc-title-country', `(${countryRef.title})`)
					]),
					m('.membersmember-desc-contact', [
						m('.membersmember-desc-contact-name', contact_name),
						email &&
							m('a.membersmember-link.membersmember-desc-contact-email',
								{href: `mailto:${email}`},
								email
							),
						website &&
							m('a.membersmember-link.membersmember-desc-contact-site',
								{
									mod: 'blue',
									href: website,
									target: '_blank',
									rel: 'noopenener nofollow'
								},
								website
							)
					]),
					m('address.membersmember-desc-address', [
						m('.membersmember-desc-address-text',
							[address, postal, city].filter(v => v).join(', ')
						),
						m('.membersmember-desc-address-phone', phone)
					])
				])
			])
		)
	}
}

const MembersTitle = ({children}) => m(Title, {heading: 4}, children)

class MembersSector extends View<{
	children: Array<Child>
	title: string
}> {
	render = this.render
	state = {open: false}
	view() {
		const {children, title} = this.attrs
		const {open} = this.state
		return (
			children.length > 0 &&
			m('.memberssector',
				m('a.memberssector-heading',
					{
						onclick: e => {
							e.preventDefault()
							this.state.open = !this.state.open
						}
					},
					[
						m(MembersTitle, [
							title,
							m('span.memberssector-heading-amount', `(${children.length})`)
						]),
						m('.memberssector-heading-toggle', open ? 'Close' : 'Open')
					]
				),
				open && children
			)
		)
	}
}

const toInt = (v: string) => parseInt(v, 10)
const sortAlphabetic = (a, b) => (a < b ? -1 : a > b ? 1 : 0)
const sortByTitle = (a, b) => sortAlphabetic(a.title, b.title)
const renderMember = member => m(MembersMember, member)
const checkSector = (id: string) => (member: Member) => {
	const sector = member.sector[0]
	if (!sector) return false
	const match = getSectorIdFromKey(sector)
	return match == id
}
const getSectorIdFromKey = (key: string) => {
	const match = /([0-9])+$/.exec(key)
	return match && match[0]
}
const includeCountry = (countries: Array<Country>) => (member: Member) => {
	return {
		...member,
		countryRef: countries.find(
			country => member.country && `${country.id}` === member.country[0]
		)
	}
}

const getCurrentMembers = (visibleMembers: Array<Member>, selectedCategory) => {
	return visibleMembers.filter(
		member =>
			!selectedCategory.size ||
			selectedCategory.has(member.type && (member.type[0] as any))
	)
}

const ITEMS_PER_PAGE = 15

export class Members extends Layout<{
	members: Array<Member>
}> {
	static viewIntroBar = () => m('.members-intro')
	state = pagedFilters(({single}) => ({
		selectedCountry: single('country', toInt),
		selectedCategory: single('category')
	}))
	view() {
		const {lists, members} = this.attrs.data
		const {selectedCountry, selectedCategory, page} = this.state
		const getMembers = id =>
			members.filter(member => member.country && member.country[0] === `${id}`)
		const countries = lists.countries
			.map(country => ({...country, members: getMembers(country.id)}))
			.sort(sortByTitle)
		const visibleMembers: Array<Member> = selectedCountry.size
			? [].concat(...selectedCountry.map(getMembers))
			: members

		const paginate = <T>(
			list: Array<T>,
			view: (list: Array<T>) => Children
		) => {
			const total = Math.ceil(list.length / ITEMS_PER_PAGE)
			return [
				view(
					list.slice(
						page.ref() * ITEMS_PER_PAGE,
						page.ref() * ITEMS_PER_PAGE + ITEMS_PER_PAGE
					)
				),
				total > 1 &&
					m(PaginationThemed, {
						current: page.ref,
						total
					})
			]
		}
		return m('.members',
			m('.members-container',
				m(MembersMap, {countries, selected: selectedCountry}),
				m(Aside,
					{
						class: 'members-overview',
						aside: () =>
							m('.members-overview-countries',
								m(MembersTitle, 'Country'),
								m(Filters,
									{filter: selectedCountry, style: 'aside'},
									m('.members-overview-countries-list',
										countries
											.filter(country => country.members.length > 0)
											.map(({id, title, members}) =>
												m(Toggle,
													{
														id,
														onclick: () => {
															const container = document.querySelector(
																'.members-overview'
															)
															if (container)
																scrollIntoView(container, {
																	block: 'start',
																	behavior: 'smooth'
																})
														}
													},
													[
														title,
														' ',
														members.length > 0 && `(${members.length})`
													]
												)
											)
									)
								)
							)
					},
					m('.members-overview-categories', [
						m('.members-overview-categories-filter',
							m(Filters,
								{filter: selectedCategory, style: 'category'},
								Object.entries(MemberType).map(([key, value]) =>
									m(Toggle, {id: key}, value)
								)
							)
						),
						selectedCategory.has('sector')
							? paginate(
									[]
										.concat(
											...Object.entries(lists.sectors).map(([id, name]) =>
												visibleMembers.filter(checkSector(id))
											)
										)
										.concat(
											visibleMembers.filter(
												vm =>
													vm.type &&
													vm.type[0] === 'sector' &&
													(!vm.sector || !vm.sector.length)
											)
										),
									list =>
										list
											.reduce((sectors, member) => {
												const current = sectors[sectors.length - 1]
												if (
													!member.sector[0] ||
													member.sector[0].length === 0
												) {
													return sectors
												}
												if (!current || member.sector[0] != current.sector)
													return [
														...sectors,
														{sector: member.sector[0], members: [member]}
													]
												current.members.push(member)
												return sectors
											}, [])
											.map(({sector, members}) =>
												m(MembersSector,
													{
														title:
															lists.sectors[getSectorIdFromKey(sector)] ||
															'Others'
													},
													members
														.map(includeCountry(countries))
														.map(renderMember)
												)
											)
							  )
							: paginate(
									getCurrentMembers(visibleMembers, selectedCategory),
									list =>
										list
											.map(includeCountry(countries))
											.sort((a, b) => {
												if (a.title < b.title) return -1
												if (a.title < b.title) return 1
												return 0
											})
											.map(member => {
												return m('.members-overview-categories-padded',
													{key: member.id},
													renderMember(member)
												)
											})
							  )
					])
				)
			)
		)
	}
}
